import React from 'react'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import AuthBasePageWrapper from '../Login/AuthBasePageWrapper'
import LoginPaper from '../Login/components/LoginPaper'
import LoginButton from '../Login/components/LoginButton'

const FEATURES = [
  {
    icon: 'fa-light fa-paintbrush-pencil',
    textKey: 'auth.disclaimer_beta_only.features.interface'
  },
  {
    icon: 'fa-regular fa-circle-check',
    textKey: 'auth.disclaimer_beta_only.features.services_status'
  },
  {
    icon: 'fa-solid fa-headset',
    textKey: 'auth.disclaimer_beta_only.features.customer_service'
  },
  {
    icon: 'fa-regular fa-square-poll-vertical',
    textKey: 'auth.disclaimer_beta_only.features.wifi_stats'
  },
  {
    icon: 'fa-regular fa-mobile-button',
    textKey: 'auth.disclaimer_beta_only.features.mobile_apps'
  },
  {
    icon: 'fa-regular fa-users',
    textKey: 'auth.disclaimer_beta_only.features.user_management'
  }
]

const APP_STORE_URL = 'https://apps.apple.com/fr/app/wifirst-center/id6447067001'
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=fr.wifirst.mycenter&hl=fr'

const FeatureItem = ({ icon, textKey }: { icon: string, textKey: string }) => {
  const theme = useTheme()
  const { t } = useTranslation()

  return (
    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
      <Box sx={{ minWidth: 24, display: 'flex', justifyContent: 'center' }}>
        <i
          className={icon}
          style={{
            color: theme.palette.primary.main,
            fontSize: 20
          }}
        />
      </Box>
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 400,
          color: 'content.primary'
        }}
      >
        {t(textKey)}
      </Typography>
    </Stack>
  )
}

const StoreButton = ({
  icon,
  textKey,
  onClick
}: {
  icon: string,
  textKey: string,
  onClick: () => void
}) => {
  const { t } = useTranslation()

  return (
    <LoginButton
      onClick={onClick}
      sx={{
        backgroundColor: '#292929 !important',
        color: 'white',
        height: '40px',
        flex: 1,
        '&:hover': {
          backgroundColor: '#404040 !important'
        }
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <i className={icon} style={{ fontSize: 16 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
          {t(textKey)}
        </Typography>
      </Stack>
    </LoginButton>
  )
}

const DisclaimerBetaOnly = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleDiscoverNow = () => {
    navigate('/')
  }

  const handleAppStoreClick = () => {
    window.open(APP_STORE_URL, '_blank')
  }

  const handleGooglePlayClick = () => {
    window.open(GOOGLE_PLAY_URL, '_blank')
  }

  return (
    <AuthBasePageWrapper>
      <LoginPaper>
        <Stack
          gap={3}
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '420px'
          }}
        >
          <Typography
            sx={{
              fontSize: '36px',
              lineHeight: '36px',
              fontWeight: 600,
              color: 'content.secondary',
              textAlign: 'center'
            }}
          >
            {t('auth.disclaimer_beta_only.title')}
          </Typography>

          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 400,
              color: 'content.primary',
              textAlign: 'center',
              mb: 2
            }}
          >
            {t('auth.disclaimer_beta_only.subtitle')}
          </Typography>

          <Box sx={{ 
            width: '100%', 
            mb: 2,
            px: '15px'
          }}>
            {FEATURES.map((feature, index) => (
              <FeatureItem
                key={index}
                icon={feature.icon}
                textKey={feature.textKey}
              />
            ))}
          </Box>

          <Box sx={{ width: '100%', pb: 0.5 }}>
            <LoginButton
              variant="contained"
              onClick={handleDiscoverNow}
              sx={{
                height: '56px',
                fontSize: '16px',
                fontWeight: 500
              }}
            >
              {t('auth.disclaimer_beta_only.discover_button')}
            </LoginButton>
          </Box>

          <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
            <StoreButton
              icon="fa-brands fa-apple"
              textKey="auth.disclaimer_beta_only.app_store"
              onClick={handleAppStoreClick}
            />
            <StoreButton
              icon="fa-brands fa-google-play"
              textKey="auth.disclaimer_beta_only.google_play"
              onClick={handleGooglePlayClick}
            />
          </Stack>
        </Stack>
      </LoginPaper>
    </AuthBasePageWrapper>
  )
}

export default DisclaimerBetaOnly
